import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiCheckCircle, FiSend, FiArrowRight
} from 'react-icons/fi';
import { FaHandshake } from 'react-icons/fa';

const benefits = [
  { icon: '💰', title: 'High Profit Margins', desc: 'Earn competitive dealer margins with volume-based incentives' },
  { icon: '🏆', title: 'Exclusive Territory', desc: 'Get dedicated territory rights with no competition within your area' },
  { icon: '📦', title: 'Assured Stock Supply', desc: 'Never worry about stock-outs with our reliable supply chain' },
  { icon: '🎯', title: 'Marketing Support', desc: 'Receive promotional materials, branding support, and campaigns' },
  { icon: '📚', title: 'Sales Training', desc: 'Regular training and development sessions for you and your team' },
  { icon: '🤝', title: 'Dedicated Support', desc: 'Your personal account manager for all business needs' },
];

const businessTypes = [
  'Retailer', 'Wholesaler', 'Sub-Distributor', 'Supermarket / Modern Trade',
  'Hotel / Restaurant', 'Institution / Canteen', 'Online Seller', 'Other',
];

const tiers = [
  { name: 'Silver Partner', icon: '🥈', monthly: '₹2L – ₹5L', deposit: '₹25,000', margin: 'Up to 8%', features: ['Assigned territory', 'Monthly visits', 'Basic POS materials', 'Payment: Advance'] },
  { name: 'Gold Partner', icon: '🥇', monthly: '₹5L – ₹15L', deposit: '₹75,000', margin: 'Up to 12%', features: ['Exclusive territory', 'Weekly visits', 'Full marketing support', 'Credit: 7 days', 'Training included'], highlight: true },
  { name: 'Platinum Partner', icon: '💎', monthly: '₹15L+', deposit: 'Negotiable', margin: 'Up to 18%', features: ['Multi-zone exclusivity', 'Dedicated manager', 'Joint promotions', 'Credit: 15–30 days', 'Priority support'] },
];

const initialForm = {
  name: '', company: '', mobile: '', email: '', city: '', state: '',
  businessType: '', experience: '', message: '',
};

export default function BecomeDealer() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.mobile.trim() || !/^[6-9]\d{9}$/.test(form.mobile)) errs.mobile = 'Enter valid 10-digit mobile number';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Enter valid email address';
    if (!form.city.trim()) errs.city = 'City is required';
    if (!form.state.trim()) errs.state = 'State is required';
    if (!form.businessType) errs.businessType = 'Select business type';
    return errs;
  };

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(er => ({ ...er, [e.target.name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setSubmitted(true); }, 1500);
  };

  const F = ({ label, name, type = 'text', placeholder, required }) => (
    <div className="form-group-c">
      <label className="form-label-c">{label} {required && <span style={{ color: 'red' }}>*</span>}</label>
      <input
        type={type}
        name={name}
        className="form-control-c"
        placeholder={placeholder}
        value={form[name]}
        onChange={handleChange}
        style={errors[name] ? { borderColor: '#dc3545' } : {}}
      />
      {errors[name] && <div style={{ color: '#dc3545', fontSize: '0.78rem', marginTop: 4 }}>{errors[name]}</div>}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Become a Dealer | Avivan Enterprises — FMCG Dealer Partnership India</title>
        <meta name="description" content="Apply to become an authorized dealer or distributor partner with Avivan Enterprises. Earn competitive margins, get exclusive territory, and grow your business." />
      </Helmet>

      {/* Page Banner */}
      <div className="page-banner">
        <div className="container">
          <div className="page-banner-content text-center">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(243,156,18,0.2)', color: '#f39c12', padding: '6px 18px', borderRadius: 50, fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, border: '1px solid rgba(243,156,18,0.3)' }}>
              <FaHandshake size={12} /> Partnership Opportunity
            </div>
            <h1>Become a Dealer</h1>
            <p>Join our growing network of authorized FMCG distribution partners across India</p>
            <div className="breadcrumb-c" style={{ justifyContent: 'center' }}>
              <Link to="/">Home</Link>
              <span className="sep">/</span>
              <span style={{ color: 'var(--accent)' }}>Become a Dealer</span>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <section style={{ padding: '90px 0' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge">🎯 Why Partner With Us</span>
            <h2 className="section-title">Benefits of <span>Dealership</span></h2>
            <p className="section-subtitle">Unlock a world of business opportunities with Avivan Enterprises dealer partnership</p>
            <div className="divider-bar" />
          </div>
          <div className="row g-4">
            {benefits.map((b, i) => (
              <div className="col-md-4 col-sm-6" key={b.title} data-aos="fade-up" data-aos-delay={i * 70}>
                <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 16, padding: 28, textAlign: 'center', height: '100%', transition: 'var(--transition)' }} className="card-hover">
                  <div style={{ fontSize: '2.6rem', marginBottom: 14 }}>{b.icon}</div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 8 }}>{b.title}</h4>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Tiers */}
      <section style={{ padding: '80px 0', background: 'var(--light)' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge">💼 Partnership Tiers</span>
            <h2 className="section-title">Choose Your <span>Partnership Level</span></h2>
            <div className="divider-bar" />
          </div>
          <div className="row g-4 justify-content-center">
            {tiers.map((t, i) => (
              <div className="col-lg-4 col-md-6" key={t.name} data-aos="fade-up" data-aos-delay={i * 100}>
                <div style={{
                  background: t.highlight ? 'var(--gradient-primary)' : '#fff',
                  border: t.highlight ? 'none' : '1.5px solid var(--border)',
                  borderRadius: 20,
                  padding: '36px 32px',
                  height: '100%',
                  position: 'relative',
                  transition: 'var(--transition)',
                  boxShadow: t.highlight ? 'var(--shadow-primary)' : 'var(--shadow-sm)',
                }} className={t.highlight ? '' : 'card-hover'}>
                  {t.highlight && (
                    <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: 'var(--accent)', color: '#fff', padding: '5px 18px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', whiteSpace: 'nowrap' }}>
                      Most Popular
                    </div>
                  )}
                  <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>{t.icon}</div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: t.highlight ? '#fff' : 'var(--dark)', marginBottom: 4 }}>{t.name}</h3>
                  <div style={{ color: t.highlight ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 20 }}>Monthly Business: <strong style={{ color: t.highlight ? 'var(--accent)' : 'var(--primary)' }}>{t.monthly}</strong></div>
                  <div style={{ background: t.highlight ? 'rgba(255,255,255,0.1)' : 'var(--light)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
                    <div style={{ fontSize: '0.75rem', color: t.highlight ? 'rgba(255,255,255,0.6)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>Margin Up To</div>
                    <div style={{ fontSize: '2rem', fontWeight: 900, color: t.highlight ? 'var(--accent)' : 'var(--primary)', fontFamily: 'Poppins, sans-serif' }}>{t.margin}</div>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, marginBottom: 24 }}>
                    {t.features.map(f => (
                      <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, color: t.highlight ? 'rgba(255,255,255,0.82)' : 'var(--dark)', fontSize: '0.88rem', padding: '6px 0', borderBottom: t.highlight ? '1px solid rgba(255,255,255,0.08)' : '1px solid var(--border)' }}>
                        <FiCheckCircle style={{ color: t.highlight ? 'var(--accent)' : 'var(--secondary)', flexShrink: 0 }} size={14} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to="#apply" className={t.highlight ? 'btn-accent-c w-100' : 'btn-outline-c w-100'} style={{ justifyContent: 'center' }}
                    onClick={e => { e.preventDefault(); document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' }); }}>
                    Apply Now <FiArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" style={{ padding: '90px 0' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="section-header" data-aos="fade-up">
                <span className="section-badge"><FiSend size={12} /> Apply Now</span>
                <h2 className="section-title">Dealer <span>Application Form</span></h2>
                <p className="section-subtitle">Fill out the form below and our partnership team will contact you within 48 hours</p>
                <div className="divider-bar" />
              </div>

              {submitted ? (
                <div data-aos="fade-up" style={{ background: '#d4edda', border: '1px solid #28A745', borderRadius: 16, padding: '48px 40px', textAlign: 'center' }}>
                  <div style={{ fontSize: '4rem', marginBottom: 16 }}>🎉</div>
                  <h3 style={{ color: '#1e7e34', fontWeight: 800, marginBottom: 12 }}>Application Submitted Successfully!</h3>
                  <p style={{ color: '#155724', lineHeight: 1.7 }}>
                    Thank you for your interest in partnering with Avivan Enterprises. Our partnership team will review your application and contact you within <strong>24-48 business hours</strong>.
                  </p>
                  <button className="btn-primary-c" style={{ marginTop: 20 }} onClick={() => { setForm(initialForm); setSubmitted(false); }}>
                    Submit Another Application
                  </button>
                </div>
              ) : (
                <div data-aos="fade-up" style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 20, padding: '40px 40px', boxShadow: 'var(--shadow-sm)' }}>
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6"><F label="Full Name" name="name" placeholder="Your full name" required /></div>
                      <div className="col-md-6"><F label="Company / Shop Name" name="company" placeholder="Business name (if any)" /></div>
                      <div className="col-md-6"><F label="Mobile Number" name="mobile" placeholder="10-digit mobile number" required /></div>
                      <div className="col-md-6"><F label="Email Address" name="email" type="email" placeholder="your@email.com" required /></div>
                      <div className="col-md-6"><F label="City" name="city" placeholder="Your city" required /></div>
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">State <span style={{ color: 'red' }}>*</span></label>
                          <select name="state" className="form-select-c" value={form.state} onChange={handleChange} style={errors.state ? { borderColor: '#dc3545' } : {}}>
                            <option value="">Select State</option>
                            {['Tamil Nadu', 'Karnataka', 'Kerala', 'Andhra Pradesh', 'Telangana', 'Maharashtra', 'Gujarat', 'Rajasthan', 'Delhi', 'Other'].map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                          {errors.state && <div style={{ color: '#dc3545', fontSize: '0.78rem', marginTop: 4 }}>{errors.state}</div>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Business Type <span style={{ color: 'red' }}>*</span></label>
                          <select name="businessType" className="form-select-c" value={form.businessType} onChange={handleChange} style={errors.businessType ? { borderColor: '#dc3545' } : {}}>
                            <option value="">Select business type</option>
                            {businessTypes.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          {errors.businessType && <div style={{ color: '#dc3545', fontSize: '0.78rem', marginTop: 4 }}>{errors.businessType}</div>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group-c">
                          <label className="form-label-c">Years of Business Experience</label>
                          <select name="experience" className="form-select-c" value={form.experience} onChange={handleChange}>
                            <option value="">Select experience</option>
                            {['Less than 1 year', '1-2 Years', '3-5 Years', '5-10 Years', '10+ Years'].map(e => <option key={e} value={e}>{e}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-group-c">
                          <label className="form-label-c">Message / Additional Info</label>
                          <textarea name="message" className="form-control-c" rows="4" placeholder="Tell us about your current business, distribution area coverage, monthly turnover, or any specific requirements..." value={form.message} onChange={handleChange} />
                        </div>
                      </div>
                    </div>
                    <div style={{ background: 'var(--light)', borderRadius: 10, padding: '14px 18px', marginBottom: 24, fontSize: '0.82rem', color: 'var(--text-muted)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <span style={{ flexShrink: 0, marginTop: 2 }}>ℹ️</span>
                      By submitting this form, you agree to be contacted by our partnership team. Your information is kept confidential and never shared with third parties.
                    </div>
                    <button type="submit" className="btn-primary-c w-100" style={{ fontSize: '1rem', padding: '15px', justifyContent: 'center' }} disabled={submitting}>
                      {submitting ? (
                        <><span className="spinner-border spinner-border-sm me-2" /> Processing...</>
                      ) : (
                        <><FiSend size={16} /> Submit Application</>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
